import { useEffect, useState } from "react";
import { ExclamationTriangleIcon, ChevronRightIcon, PlusIcon } from "@heroicons/react/20/solid";
import { formatDistanceToNowStrict, getTime, isSameDay, isValid } from "date-fns";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  FaceSmileIcon,
  FlagIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ClockIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import ActivityAction from "../actions/ActivityAction";
import { useDispatch } from "react-redux";
import { fromJS } from "immutable";
import { searchActivities, markAsDoneActivity, updateTaskField } from "../api/ProjectApi";
import { ActivityTypes } from "../constants/action-types";
import { useTranslation } from "react-i18next";

export const renderActivityIcon = (activity) => {
  switch (activity.getIn(["event", "data", "activityType"])) {
    case ActivityTypes.email:
    case ActivityTypes.logEmail:
    case ActivityTypes.sendEmail:
    case ActivityTypes.sendDraftEmail:
    case ActivityTypes.sendScheduledEmail:
    case ActivityTypes.recordSentEmail:
      return <EnvelopeIcon className={"w-5"} />;
    case ActivityTypes.task:
      return <ClockIcon className={"w-5"} />;
    case ActivityTypes.meeting:
      return <CalendarDaysIcon className={"w-5"} />;
    case ActivityTypes.deadline:
      return <FlagIcon className={"w-5"} />;
    case ActivityTypes.lunch:
      return <FaceSmileIcon className={"w-5"} />;
    case ActivityTypes.call:
      return <PhoneIcon className={"w-5"} />;
  }
};

export const DueDateDisplayField = ({ dateTimeString }: any) => {
  const { t } = useTranslation();
  const today = new Date();
  const endDate = new Date(dateTimeString);
  if (!isValid(endDate)) {
    return null;
  }
  if (isSameDay(today, endDate)) {
    return <div>{t("Today")}</div>;
  }
  if (getTime(today) > getTime(endDate)) {
    return (
      <div className={"text-red-500"} title={t("Overdue")}>
        {t("Overdue")}: {formatDistanceToNowStrict(endDate, { unit: "day", addSuffix: false })}
      </div>
    );
  }
  return (
    <div title={t("Upcoming")}>
      {t("Due in")} {formatDistanceToNowStrict(endDate, { unit: "day" })}
    </div>
  );
};

export const ActivityStatus = ({ onClickMarkAsDone, activity, itemId }) => {
  const [isDone, setIsDone] = useState(activity.getIn(["event", "data", "done"]) || false);
  const [showPulse, setShowPulse] = useState(false);
  const [pulseColor, setPulseColor] = useState("bg-green-400");
  useEffect(() => {
    setIsDone(activity.getIn(["event", "data", "done"]) || false);
  }, [activity]);

  const handleMarkAsDone = (activity, status) => {
    setIsDone(status);
    setPulseColor(status ? "bg-green-300" : "bg-gray-300");
    setShowPulse(true);
    setTimeout(() => setShowPulse(false), 150);
    markAsDoneActivity(itemId, activity.get("id"), status).then(() => {
      onClickMarkAsDone();
    });
  };

  if (isDone) {
    return (
      <div className={"relative inline-flex items-center justify-center"}>
        {showPulse && (
          <span
            className={`absolute inline-flex h-5 w-5 rounded-full ${pulseColor} opacity-30 animate-ping`}
          />
        )}
        <CheckCircleIcon
          className={
            "relative w-6 text-green-600 cursor-pointer transition duration-100 ease-out hover:scale-105 active:scale-95"
          }
          onClick={() => {
            handleMarkAsDone(activity, false);
          }}
        />
      </div>
    );
  }
  return (
    <div className={"relative inline-flex items-center justify-center"}>
      {showPulse && (
        <span
          className={`absolute inline-flex h-5 w-5 rounded-full ${pulseColor} opacity-30 animate-ping`}
        />
      )}
      <CheckCircleIcon
        className={
          "relative w-6 text-gray-400 cursor-pointer transition duration-100 ease-out hover:scale-105 active:scale-95"
        }
        onClick={() => {
          handleMarkAsDone(activity, true);
        }}
      />
    </div>
  );
};

const ActivityIndicator = ({ deal }) => {
  const nextActivityDatetime = new Date(deal.getIn(["data", "nextActivityDatetime", "value"]));
  const stage = deal.getIn(["data", "stage", "value"]);
  const [itemLogs, setItemLogs] = useState(fromJS([]));
  const dispatch = useDispatch();

  const updateNextActivityDatetime = (value) => {
    updateTaskField(deal.get("id"), "nextActivityDatetime", value).then(() => {});
  };

  const loadActivities = () => {
    searchActivities({
      itemId: deal.get("id"),
      scope: "item",
      includeDependencies: true,
      status: "false",
      eventActions: "createActivity",
    }).then((logs) => {
      const activities = fromJS(logs).filter(isActivityType);
      const firstActivityEnd = `${activities.getIn([
        0,
        "event",
        "data",
        "end",
      ])}T${activities.getIn([0, "event", "data", "endTime"])}`;
      const firstActivityDueDatetime = new Date(firstActivityEnd);
      if (isValid(firstActivityDueDatetime)) {
        updateNextActivityDatetime(firstActivityEnd);
      } else {
        updateNextActivityDatetime("");
      }
      setItemLogs(activities);
    });
  };

  const isActivityType = (activity) => {
    return Object.keys(ActivityTypes).includes(activity.getIn(["event", "data", "activityType"]));
  };

  const renderDueDateField = (activity) => {
    const endDateTimeString = `${activity.getIn(["event", "data", "end"])}T${activity.getIn([
      "event",
      "data",
      "endTime",
    ])}`;
    return <DueDateDisplayField dateTimeString={endDateTimeString} />;
  };

  const renderActivityCompletingStatus = (activity) => {
    return (
      <ActivityStatus
        activity={activity}
        itemId={deal.get("id")}
        onClickMarkAsDone={() => {
          loadActivities();
        }}
      />
    );
  };

  const renderSummary = (activity) => {
    if (activity.getIn(["event", "data", "done"])) {
      return <span className={"stroke-1"}>{activity.getIn(["event", "data", "summary"])}</span>;
    }
    return <span>{activity.getIn(["event", "data", "summary"])}</span>;
  };

  const renderNoActivities = () => {
    return (
      <div className="py-1">
        <div className={"px-2 py-2 flex items-center space-x-2 cursor-default"}>
          <div className={"pt-2 space-y-1 w-40 text-ellipsis overflow-hidden ..."}>
            <div className={"w-full px-4"}>No activity</div>
          </div>
        </div>
      </div>
    );
  };

  const renderActivities = () => {
    const activities = itemLogs.filter(isActivityType);
    if (activities.size == 0) {
      return renderNoActivities();
    }
    return activities.map((activity) => (
      <div key={`activity_${activity.get("id")}`}>
        <div className={"px-2 py-1 flex items-center space-x-2 cursor-default"}>
          <div>{renderActivityCompletingStatus(activity)}</div>
          <div className={"pt-2 space-y-1 w-40 text-ellipsis overflow-hidden ..."}>
            {renderSummary(activity)}
            <div className={"text-xs space-y-2"}>
              {renderDueDateField(activity)}
              <div className={"text-gray-400"}>{activity.getIn(["meta", "contact", "label"])}</div>
            </div>
          </div>
          <div>{renderActivityIcon(activity)}</div>
        </div>
      </div>
    ));
  };

  const renderStatus = () => {
    const isValidDateTime = isValid(nextActivityDatetime);
    let statusCSS = "bg-gray-100 text-gray-400 hover:text-gray-600 focus:ring-offset-gray-100";
    let icon = <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />;
    let title = "";
    if (!isValidDateTime) {
      icon = <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />;
      statusCSS = "bg-yellow-100 text-yellow-400 hover:text-yellow-600 focus:ring-offset-gray-100";
      title = "No activity";
    } else {
      if (getTime(Date.now()) > getTime(nextActivityDatetime)) {
        icon = <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />;
        title = "Due date activities";
        statusCSS = "bg-red-600 text-white hover:text-white-200 focus:ring-offset-gray-100";
      }
      if (isSameDay(Date.now(), nextActivityDatetime)) {
        title = "On track";
        icon = <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />;
        statusCSS = "bg-green-600 text-white hover:text-white-200 focus:ring-offset-gray-100";
      }
    }
    return (
      <Popover>
        <PopoverButton
          title={title}
          className={`${statusCSS} flex items-center rounded-full outline-none focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
          onClick={() => {
            loadActivities();
          }}
        >
          {icon}
        </PopoverButton>
        <PopoverPanel
          anchor="bottom start"
          className="z-20 mt-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
        >
          {renderActivities()}
          <div className="py-1">
            <div>
              <button
                type="submit"
                onClick={() => {
                  const dealId = deal.get("id");
                  const dealLabel = deal.getIn(["data", "name", "value"]);
                  const dealType = deal.get("type");
                  if (dealId && dealLabel && dealType) {
                    dispatch(
                      ActivityAction.showActivityDialog(dealId, null, {
                        item: {
                          value: dealId,
                          label: dealLabel,
                          type: dealType,
                        },
                        activityType: ActivityTypes.meeting,
                        summary: "Meeting",
                      })
                    );
                  }
                }}
                className={
                  "cursor-pointer w-full px-4 py-2 text-left text-sm flex items-center text-gray-700 space-x-2 hover:text-blue-500"
                }
              >
                <PlusIcon className={"w-4"} />
                <div className={""}>Schedule an activity</div>
              </button>
            </div>
          </div>
        </PopoverPanel>
      </Popover>
    );
  };

  if (stage == "Won") {
    return null;
  }

  return renderStatus();
};

export default ActivityIndicator;
